import { ArrowRightLeft, LoaderCircle, Mic, MicOff, PhoneOff, PanelBottomOpen } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getCallTransferTargets, transferCall } from '../services/callTransfers';

function CallExperienceOverlay({
  callState = 'idle',
  participant = null,
  isMuted = false,
  onToggleMute,
  onHangUp,
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showKeypadHint, setShowKeypadHint] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTab, setTransferTab] = useState('user');
  const [targets, setTargets] = useState({ users: [], queues: [] });
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsLoaded, setTargetsLoaded] = useState(false);
  const [targetsError, setTargetsError] = useState('');
  const [targetsRequestVersion, setTargetsRequestVersion] = useState(0);
  const [targetId, setTargetId] = useState('');
  const [extension, setExtension] = useState('');
  const [transferError, setTransferError] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    if (callState !== 'in-call') {
      setElapsedSeconds(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setElapsedSeconds((current) => current + 1);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [callState]);

  useEffect(() => {
    if (!showKeypadHint) return undefined;

    const timeoutId = window.setTimeout(() => {
      setShowKeypadHint(false);
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [showKeypadHint]);

  const initials = useMemo(
    () => getInitials(participant?.name || participant?.number || 'Call'),
    [participant?.name, participant?.number]
  );

  const title = participant?.name || participant?.number || 'Unknown caller';
  const subtitle = participant?.name && participant?.number ? participant.number : participant?.label || '';
  const statusLabel = getOverlayStatus(callState);
  const isLiveCall = callState === 'in-call';
  const canTransfer = isLiveCall
    && participant?.direction === 'incoming'
    && participant?.routeType !== 'internal'
    && Boolean(participant?.callId);

  useEffect(() => {
    if (!showTransfer || !canTransfer) return undefined;
    let active = true;
    setTargets({ users: [], queues: [] });
    setTargetsLoading(true);
    setTargetsLoaded(false);
    setTargetsError('');
    setTransferError('');
    setTargetId('');
    setExtension('');
    getCallTransferTargets(participant.callId)
      .then((payload) => {
        if (!active) return;
        setTargets({ users: payload?.users || [], queues: payload?.queues || [] });
        setTargetsLoaded(true);
      })
      .catch((error) => {
        if (active) setTargetsError(getTransferErrorMessage(error));
      })
      .finally(() => {
        if (active) setTargetsLoading(false);
      });
    return () => { active = false; };
  }, [canTransfer, participant?.callId, showTransfer, targetsRequestVersion]);

  const availableTargets = transferTab === 'user' ? targets.users : targets.queues;
  const hasSelectedTarget = transferTab === 'extension'
    ? Boolean(extension.trim())
    : availableTargets.some((target) => target.id === targetId);
  const canSubmitTransfer = targetsLoaded
    && !targetsLoading
    && !targetsError
    && hasSelectedTarget
    && !isTransferring;

  const submitTransfer = async () => {
    if (isTransferring) return;
    const target = transferTab === 'extension'
      ? { targetType: 'extension', extension: extension.trim() }
      : { targetType: transferTab, [transferTab === 'user' ? 'targetUserId' : 'targetQueueId']: targetId };
    if (!(target.extension || target.targetUserId || target.targetQueueId)) {
      setTransferError('Select a transfer destination.');
      return;
    }
    setIsTransferring(true);
    setTransferError('');
    try {
      const operationId = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      await transferCall(participant.callId, { ...target, operationId });
      setShowTransfer(false);
    } catch (error) {
      setTransferError(getTransferErrorMessage(error));
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <div className={`call-experience-overlay${isLiveCall ? ' is-live' : ''}`}>
      <div className="call-experience-card">
        <div className="call-experience-top">
          <div className={`call-experience-avatar${participant?.direction === 'incoming' ? ' is-incoming' : ''}`}>
            {initials}
          </div>

          <div className="call-experience-copy">
            <div className="call-experience-eyebrow">
              <span className={`call-experience-status-dot is-${callState === 'in-call' ? 'connected' : 'progress'}`} />
              <span>{statusLabel}</span>
              {callState === 'connecting' ? <LoaderCircle size={14} className="call-experience-spinner" /> : null}
            </div>
            <div className="call-experience-title">{title}</div>
            {subtitle ? <div className="call-experience-subtitle">{subtitle}</div> : null}
          </div>
        </div>

        {isLiveCall ? (
          <>
            <div className="call-experience-timer">{formatCallDuration(elapsedSeconds)}</div>

            <div className="call-experience-actions">
              <button
                type="button"
                className={`call-experience-action${isMuted ? ' is-active' : ''}`}
                onClick={onToggleMute}
              >
                {isMuted ? <MicOff size={17} /> : <Mic size={17} />}
                <span>{isMuted ? 'Muted' : 'Mute'}</span>
              </button>

              <button
                type="button"
                className="call-experience-action"
                onClick={() => setShowKeypadHint(true)}
              >
                <PanelBottomOpen size={17} />
                <span>Keypad</span>
              </button>

              {canTransfer ? (
                <button type="button" className="call-experience-action" onClick={() => setShowTransfer(true)}>
                  <ArrowRightLeft size={17} />
                  <span>Transfer</span>
                </button>
              ) : null}

              <button
                type="button"
                className="call-experience-action is-danger"
                onClick={onHangUp}
              >
                <PhoneOff size={17} />
                <span>Hang up</span>
              </button>
            </div>

            {showKeypadHint ? (
              <div className="call-experience-hint">Keypad controls are not available yet.</div>
            ) : null}

            {showTransfer ? (
              <div className="call-transfer-dialog" role="dialog" aria-modal="true" aria-label="Transfer call">
                <div className="call-transfer-title">Blind transfer</div>
                <div className="call-transfer-tabs" role="tablist" aria-label="Transfer destination type">
                  {['user', 'queue', 'extension'].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={transferTab === tab}
                      className={transferTab === tab ? 'is-active' : ''}
                      onClick={() => { setTransferTab(tab); setTargetId(''); setTransferError(''); }}
                    >
                      {tab === 'user' ? 'Users' : tab === 'queue' ? 'Queues' : 'Extension'}
                    </button>
                  ))}
                </div>
                {transferTab === 'extension' ? (
                  <input
                    aria-label="Extension"
                    value={extension}
                    onChange={(event) => setExtension(event.target.value)}
                    placeholder="Enter extension"
                  />
                ) : (
                  <select
                    aria-label={transferTab === 'user' ? 'Transfer user' : 'Transfer queue'}
                    value={targetId}
                    onChange={(event) => setTargetId(event.target.value)}
                    disabled={targetsLoading || !targetsLoaded || Boolean(targetsError)}
                  >
                    <option value="">Select {transferTab}</option>
                    {availableTargets.map((target) => (
                      <option key={target.id} value={target.id}>{target.name}</option>
                    ))}
                  </select>
                )}
                {targetsLoading ? <div className="call-transfer-state">Loading targets...</div> : null}
                {targetsLoaded && transferTab !== 'extension' && availableTargets.length === 0 ? (
                  <div className="call-transfer-state">
                    {transferTab === 'user' ? 'No eligible users available' : 'No eligible queues available'}
                  </div>
                ) : null}
                {targetsError ? (
                  <div className="call-transfer-error" role="alert">
                    <span>{targetsError}</span>
                    <button
                      type="button"
                      className="call-transfer-retry"
                      onClick={() => setTargetsRequestVersion((version) => version + 1)}
                    >
                      Retry
                    </button>
                  </div>
                ) : null}
                {transferError ? <div className="call-transfer-error" role="alert">{transferError}</div> : null}
                <div className="call-transfer-actions">
                  <button type="button" onClick={() => setShowTransfer(false)} disabled={isTransferring}>Cancel</button>
                  <button type="button" className="is-primary" aria-label="Confirm transfer" onClick={submitTransfer} disabled={!canSubmitTransfer}>
                    {isTransferring ? 'Transferring...' : 'Transfer'}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="call-experience-footer">
            <button
              type="button"
              className="call-experience-cancel"
              onClick={onHangUp}
            >
              <PhoneOff size={16} />
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function getTransferErrorMessage(error) {
  const message = String(error?.message || '').trim();
  if (!message) return 'Unable to complete the transfer. Please try again.';
  if (/outcome is uncertain|reconcil/i.test(message)) {
    return 'Transfer status is uncertain. Keep the call connected and contact an administrator before trying again.';
  }
  if (/callback URL is not configured/i.test(message)) {
    return 'Call transfer is temporarily unavailable.';
  }
  if (/operationId|callSid|rootCall|TwiML/i.test(message)) {
    return 'Unable to complete the transfer. Please try again.';
  }
  return message;
}

function getOverlayStatus(callState) {
  switch (callState) {
    case 'ringing':
      return 'Ringing';
    case 'in-call':
      return 'Connected';
    case 'connecting':
    default:
      return 'Calling...';
  }
}

function getInitials(value) {
  const words = String(value || '')
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return 'CL';

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}

function formatCallDuration(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export default CallExperienceOverlay;
